/**
 * RecommendationPanel — "Recommended for You" section on My Learning page.
 *
 * EPIC-08-10b
 *
 * Behaviour:
 * - AC-1: Shows up to 5 ranked recommendation cards when agent has ≥ 3 sessions.
 * - AC-2: reason_key is localised via react-i18next.
 * - AC-3: Panel is not rendered when courses list is empty (insufficient data).
 * - AC-4: "Start" button triggers self-enroll; button switches to "Continue" on success.
 * - AC-5: Already-enrolled courses show "Continue" instead of "Start".
 * - AC-6: Skeleton placeholders shown during loading.
 * - AC-7: API errors suppress the panel silently (no error banner).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecommendations } from '../../hooks/useRecommendations';
import type { Recommendation } from '../../types';
import styles from './RecommendationPanel.module.css';

interface RecommendationPanelProps {
  enrolledCourseIds?: Set<string>;
  onEnroll?: (courseId: string) => Promise<void>;
  onNavigate?: (courseId: string) => void;
}

export function RecommendationPanel({
  enrolledCourseIds = new Set(),
  onEnroll,
  onNavigate,
}: RecommendationPanelProps): JSX.Element | null {
  const { t } = useTranslation('training');
  const { recommendations, isLoading, error } = useRecommendations();
  const [enrollingId, setEnrollingId] = useState<string | null>(null);
  const [enrolledIds, setEnrolledIds] = useState<Set<string>>(new Set(enrolledCourseIds));
  const [toast, setToast] = useState<string | null>(null);

  // AC-7: silently hide on error
  if (error) {
    return null;
  }

  // AC-6: skeleton while loading
  if (isLoading) {
    return (
      <section
        className={styles.panel}
        aria-label={t('recommendations.panelTitle')}
        aria-busy="true"
      >
        <h2 className={styles.heading}>{t('recommendations.panelTitle')}</h2>
        <div className={styles.grid}>
          {[0, 1, 2].map((i) => (
            <div key={i} className={`${styles.card} ${styles.skeleton}`} aria-hidden="true" />
          ))}
        </div>
      </section>
    );
  }

  // AC-3: hide when no recommendations
  if (recommendations.length === 0) {
    return null;
  }

  async function handleStart(rec: Recommendation): Promise<void> {
    if (!onEnroll) return;
    setEnrollingId(rec.course_id);
    try {
      await onEnroll(rec.course_id);
      setEnrolledIds((prev) => new Set([...prev, rec.course_id]));
      setToast(t('recommendations.enrolledToast'));
      setTimeout(() => setToast(null), 3000);
    } finally {
      setEnrollingId(null);
    }
  }

  return (
    <section className={styles.panel} aria-label={t('recommendations.panelTitle')}>
      <h2 className={styles.heading}>{t('recommendations.panelTitle')}</h2>

      {toast && (
        <div role="status" className={styles.toast}>
          {toast}
        </div>
      )}

      <div className={styles.grid}>
        {recommendations.map((rec) => {
          const isEnrolled = enrolledIds.has(rec.course_id);
          const isEnrolling = enrollingId === rec.course_id;

          return (
            <article key={rec.course_id} className={styles.card}>
              <div className={styles.cardHeader}>
                <span className={styles.typeBadge}>{rec.type}</span>
                <span className={styles.duration}>
                  {rec.estimated_duration_mins} {t('recommendations.mins')}
                </span>
              </div>

              <h3 className={styles.courseTitle}>{rec.title}</h3>

              <p className={styles.reason}>
                {t(`recommendation.reason.${rec.reason_key}`)}
              </p>

              {isEnrolled ? (
                <button
                  className={styles.btnContinue}
                  onClick={() => onNavigate?.(rec.course_id)}
                >
                  {t('recommendations.continueButton')}
                </button>
              ) : (
                <button
                  className={styles.btnStart}
                  disabled={isEnrolling}
                  onClick={() => void handleStart(rec)}
                >
                  {isEnrolling ? t('common.loading') : t('recommendations.startButton')}
                </button>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
